import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {Store} from '@ngrx/store';
import {ViewportSelectors} from '@aviary/store/viewport/viewport.selectors';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {select} from '@aviary/store/store.helpers';

@Component({
  selector: 'mx-page-number-controls',
  host: {display: 'contents'},
  templateUrl: './page-number-controls.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, NgClass]
})
export class PageNumberControlsComponent {
  // Input signals that update when the length, pageSize, or currentPage changes in the paginator component.
  readonly length = input<number>(0);
  readonly pageSize = input<number>(0);
  readonly currentPage = input<number>(0);
  public readonly pageSelected = output<number>(); // Output signal that emits the selected page number.
  // Local signal to track the viewport's current size.
  public readonly isMdAndUp = toSignal(
    select(ViewportSelectors.isMdAndUp),
    {initialValue: true}
  );

  public readonly maxPageButtons: number = 3; // Maximum number of page buttons to show (not including first/last on md+ screens)
  private readonly pageButtonPadding: number = 1; // Number of page buttons to show on either side of the current page
  private readonly store: Store = inject(Store);


  /**
   * Recalculate totalPages when the page size or results length get updated.
   */
  readonly totalPages = computed<number>(() => {
    const pageSize = this.pageSize();
    return pageSize > 0 ? Math.ceil(this.length() / pageSize) : 0;
  });

  /**
   * Recalculate lastPage when totalPages gets updated.
   */
  readonly lastPage = computed<number>(() =>
    Math.max(this.totalPages() - 1, 0),
  );

  /**
   * Creates a "window" of page numbers to be rendered in the paginator.  Recalculates when the total pages, current page, or viewport size changes.
   */
  readonly middlePages = computed<number[]>(() => {
    const totalPages = this.totalPages();
    const currentPage = this.currentPage();
    this.isMdAndUp();

    // If we have any pages...
    if (totalPages > 0) {

      // Everything fits. Return and show every page.
      if (totalPages <= this.maxPageButtons) {
        return Array.from(
          {length: totalPages},
          (_, index) => index
        );
      }

      // If not everything fits in the allotted button space, calculate the window of visible page numbers.
      let start = currentPage - this.pageButtonPadding;
      let end = currentPage + this.pageButtonPadding;

      // Shift the window right when we're too close to the beginning.
      if (start < 0) {
        end -= start;
        start = 0;
      }

      // Shift the window left when we're too close to the end.
      if (end > this.lastPage()) {
        start -= end - this.lastPage();
        end = this.lastPage();
      }

      const pages = Array.from(
        {length: end - start + 1},
        (_, index) => start + index
      );

      // On md+, the template renders the first and last pages separately, so don't include them here.
      if (this.isMdAndUp()) {
        return pages.filter(
          page => page !== 0 && page !== this.lastPage()
        );
      }

      return pages;
    }
    return [];
  });

  /**
   * Decide whether to show dots between the first page and the rest of the page buttons.  Recalculates when totalPages, currentPage, or viewport size changes.
   */
  readonly showStartDots = computed<boolean>(() => {
    if (!this.isMdAndUp() || this.totalPages() <= this.maxPageButtons) { return false; }

    const pages = this.middlePages();
    return pages.length > 0 && pages[0] > 1;
  });

  /**
   * Decide whether to show dots between the last page and the rest of the page buttons.  Recalculates when totalPages, currentPage, or viewport size changes.
   */
  readonly showEndDots = computed<boolean>(() => {
    if (!this.isMdAndUp() || this.totalPages() <= this.maxPageButtons) { return false; }

    const pages = this.middlePages();

    return pages.length > 0 && pages[pages.length - 1] < this.lastPage() - 1;
  });

  /**
   * Page changed from clicking a page number button.  Update the parent directive and paginator.
   */
  selectPage(page: number): void {
    this.pageSelected.emit(page);
  }
}
